import { DeltaIndicatorType } from "../../components/delta-indicator-chip";
import { InfoCardVariants } from "../../components/info-card";
import {
  CommercialODFinancialStats,
  MultiMonthFinancialStats,
  OverallMonthlyFinancialStatsType,
  SummaryStats,
} from "../../dto";
import dispatch, { MiddlewareAction } from "../../middleware";
import { RoutePaths } from "../../models";
import { isValidNumberOrFallback } from "../../utils";
import { getMonthTypeInputValueFromDate } from "../portfolio/helper";
import { OneMonthFinancialStats } from "./../../dto/index";
import {
  CommercialOdLoanStatsSection,
  MultiMonthLoanStatsSection,
  OneMonthLoanStatsSection,
  OverallMonthlyFinancialStatsSection,
  SummaryStatsSection,
} from "./types";

export function getSummarySection(
  summaryStats: SummaryStats,
): SummaryStatsSection {
  return {
    sectionName: "Summary",
    lastUpdateAt: summaryStats.metadata.cacheLastUpdatedAt,
    infoCards: [
      {
        label: "User",
        content: summaryStats.data.totalUsers,
        navigateTo: RoutePaths.USERS,
        delta: getDeltaFromNumber(summaryStats.data.usersIncrementPercent),
        type: "SUMMARY",
      },
      {
        label: "Loans",
        content: summaryStats.data.totalLoans,
        navigateTo: RoutePaths.PERSONAL_LOANS,
        delta: getDeltaFromNumber(summaryStats.data.loansIncrementPercent),
        type: "SUMMARY",
      },
    ],
  };
}

export function getOverallMonthlyFinancialSection(
  overallMonthlyFinancialStats: OverallMonthlyFinancialStatsType,
): OverallMonthlyFinancialStatsSection {
  return {
    sectionName: "Overall Monthly Financials",
    infoCards: [
      {
        label: "Total Disbursals",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallTotalDisbursedAmount,
        ),
        type: "OVERALL_MONTHLY",
      },
      {
        label: "Processing Fees",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallTotalProcessingFees,
        ),
        type: "OVERALL_MONTHLY",
      },
      {
        label: "Closing Balance",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.closingBalance,
        ),
        type: "OVERALL_MONTHLY",
      },
      {
        label: "Total Collections",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallTotalCollections,
        ),
        type: "OVERALL_MONTHLY",
      },
      {
        label: "Principal Collected",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallTotalPrincipalCollected,
        ),
        type: "OVERALL_MONTHLY",
      },
      {
        label: "Interest Collected",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallTotalInterestEarned,
        ),
        type: "OVERALL_MONTHLY",
      },
      // INFO: Commenting this for DD Call
      // {
      //   label: "Unpaid Commitments",
      //   content: isValidNumberOrFallback(
      //     overallMonthlyFinancialStats.unpaidCommitment
      //   ),
      //   type: "OVERALL_MONTHLY",
      // },
      // INFO: Commenting this because shitty backend code (1 Aug 2025)
      // {
      //   label: "Closing balance w/o loans",
      //   content: isValidNumberOrFallback(
      //     overallMonthlyFinancialStats.closingBalanceWrittenOffLoans
      //   ),
      //   type: "OVERALL_MONTHLY",
      // },
      {
        label: "Weighted Average APR",
        content: isValidNumberOrFallback(
          overallMonthlyFinancialStats.overallWeightedAverageApr,
        ),
        type: "OVERALL_MONTHLY",
      },
    ],
  };
}

export function getOneMonthLoanSection(
  oneMonthFinancialStats: OneMonthFinancialStats,
): OneMonthLoanStatsSection {
  return {
    sectionName: "1 Month Based Loans",
    infoCards: [
      {
        label: "Total Disbursals",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.totalDisbursedAmount,
        ),
        type: "ONE_MONTH_LOAN",
      },
      {
        label: "Processing Fees",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.totalProcessingFee,
        ),
        type: "ONE_MONTH_LOAN",
      },
      {
        label: "Total Collections",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.totalRepaidAmount,
        ),
        type: "ONE_MONTH_LOAN",
      },
      {
        label: "Principal Collected",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.totalRepaidPrincipal,
        ),
        type: "ONE_MONTH_LOAN",
      },
      {
        label: "Interest Collected",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.totalRepaidInterest,
        ),
        type: "ONE_MONTH_LOAN",
      },
      {
        label: "Weighted Average APR",
        content: isValidNumberOrFallback(
          oneMonthFinancialStats.data.weightedAverageApr,
        ),
        type: "ONE_MONTH_LOAN",
      },
    ],
  };
}

export function getMultiMonthLoanSection(
  multiMonthFinancialStats: MultiMonthFinancialStats,
): MultiMonthLoanStatsSection {
  return {
    sectionName: "Multi Month Based Loans",
    infoCards: [
      {
        label: "Total Disbursals",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.totalDisbursedAmount,
        ),
        type: "MULTI_MONTH_LOAN",
      },
      {
        label: "Processing Fees",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.totalProcessingFee,
        ),
        type: "MULTI_MONTH_LOAN",
      },
      {
        label: "Total Collections",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.totalRepaidAmount,
        ),
        type: "MULTI_MONTH_LOAN",
      },
      {
        label: "Principal Collected",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.totalRepaidPrincipal,
        ),
        type: "MULTI_MONTH_LOAN",
      },
      {
        label: "Interest Collected",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.totalRepaidInterest,
        ),
        type: "MULTI_MONTH_LOAN",
      },
      {
        label: "Weighted Average APR",
        content: isValidNumberOrFallback(
          multiMonthFinancialStats.data.weightedAverageApr,
        ),
        type: "MULTI_MONTH_LOAN",
      },
    ],
  };
}

export function getCommercialOdLoanSection(
  commercialOdFinancialStats: CommercialODFinancialStats,
): CommercialOdLoanStatsSection {
  return {
    sectionName: "B2B Lending",
    infoCards: [
      {
        label: "New Sanctioned Loans",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalSanctionedLoans,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "New Sanctioned Amount",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalSanctionedAmount,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "Total Amount Disbursed",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalDisbursedAmount,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "Principal Collected",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalPrincipalRepaid,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "Total Interest Repaid",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalInterestRepaid,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "Total Processing Fee",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalProcessingFeeCharged,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "TDS Deducted - PF",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalTdsChargedOnProcessingFee,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
      {
        label: "TDS Deducted - Interest",
        content: isValidNumberOrFallback(
          commercialOdFinancialStats.data.totalTdsChargedOnInterest,
        ),
        type: "COMMERCIAL_OD_LOAN",
      },
    ],
  };
}

function getDeltaFromNumber(increment: number = 0): {
  type: DeltaIndicatorType;
  value: string;
} {
  const deltaType: DeltaIndicatorType =
    increment > 0 ? "increment" : increment < 0 ? "decrement" : "increment";
  return { type: deltaType, value: `${Math.abs(increment)}%` };
}

export function getInfoCardVariantByDelta(
  deltaType?: DeltaIndicatorType,
): InfoCardVariants {
  if (deltaType === "increment") return "success";
  if (deltaType === "decrement") return "warning";
  return "info";
}

export const fetchDataForSelectedMonth = async (
  actionCreator: (month: string, year: string) => MiddlewareAction,
  storeDispatch: any,
  selectedDate: Parameters<typeof getMonthTypeInputValueFromDate>[0],
) => {
  const monthYear = getMonthTypeInputValueFromDate(selectedDate);
  if (!monthYear) return;
  const [year, month] = monthYear.split("-");
  await dispatch(storeDispatch, actionCreator(month, year));
};

export const getSafeSum = (...values: (number | null | undefined)[]) => {
  let sum = 0;
  let count = 0;
  for (const val of values) {
    if (typeof val === "number" && Number.isFinite(val)) {
      sum += val;
      count += 1;
    }
  }
  return count === 0 ? undefined : sum;
};
